import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAddLocationMutation,
  useUpdateLocationsMutation,
  useValidatePatchLocationMutation,
  useValidatePostLocationMutation,
} from '../../services/location.api';
import { Company, Location, SiteType } from '../../stores/types';
import { InternalErrorCode } from '../../stores/types/error.interface';
import { LocationValidationError } from '../../stores/types/location.interface';
import { isSiteTypeValid } from '../../stores/types/sitetype.interface';

import useDebounce from '../../hooks/useDebounce';
import { Country, State } from '../../lib/country-state';
import type { ICountry, IState } from '../../lib/country-state/interface';
import {
  AddressAutocomplete,
  Button,
  ButtonSize,
  ButtonType,
  FormInput,
  SiteTypeDropdown,
  useGlobalModalContext,
} from '../_ui';
import type { AddressValue } from '../_ui/address/AddressAutocomplete.component';
import { Form } from '../_ui/Form.component';
import { ValidationType } from '../_ui/Input.component';
import { getCountriesByPreference } from '../Charger/utils';
import './Location.css';

interface Props {
  defaultLocation?: Location;
  company: Company | undefined;
}

const TAXABLE_COUNTRIES = ['US', 'CA'];

export const LocationEditor = memo(({ defaultLocation, company }: Props) => {
  const { t } = useTranslation();
  const { hideModal } = useGlobalModalContext();
  const [triggerAddLocation, newLocationResult] = useAddLocationMutation();
  const [triggerUpdateLocation, updateLocationResult] = useUpdateLocationsMutation();

  const [location, setLocation] = useState<Location>(defaultLocation || ({} as Location));
  const debouncedLocation = useDebounce(location, 500);
  const [activeValidationErrorCode, setActiveValidationErrorCode] = useState<string | null>(null);
  const [validationCompleted, setValidationCompleted] = useState(false);
  const isNew = useMemo(() => {
    return !defaultLocation;
  }, [defaultLocation]);

  const provinceList = useMemo(() => {
    return State.getStatesOfCountry(company?.isoCountryCode || 'CA').map((obj: IState) => ({
      ...obj,
      id: obj.isoCode,
      selected: obj.isoCode === location?.stateOrRegion,
    }));
  }, [company?.isoCountryCode, location?.stateOrRegion]);

  const [triggerValidatePostLocation, validatePostLocationResult] =
    useValidatePostLocationMutation();

  const [triggerValidatePatchLocation, validatePatchLocationResult] =
    useValidatePatchLocationMutation();

  const triggerValidateLocation = isNew
    ? triggerValidatePostLocation
    : triggerValidatePatchLocation;

  const validateLocationResult = isNew ? validatePostLocationResult : validatePatchLocationResult;

  const handleLocationChange = (key: string, value: any) => {
    const updatedLocation = {
      ...location,
      [key]: value,
      isoCountryCode: company?.isoCountryCode || '',
    };

    setLocation(updatedLocation);
  };

  useEffect(() => {
    const { name, street, streetNumber, city, zipOrPostalCode, latitude, longitude, timeZone } =
      debouncedLocation;

    const readyToValidate =
      name &&
      street &&
      streetNumber &&
      city &&
      zipOrPostalCode &&
      latitude &&
      longitude &&
      timeZone;

    if (readyToValidate) {
      triggerValidateLocation(debouncedLocation);
    }
  }, [debouncedLocation, triggerValidateLocation]);

  const country = useMemo((): ICountry | undefined => {
    const countries = Country.getAllCountries();
    const preferred = getCountriesByPreference(countries) as ICountry[] | undefined;
    return preferred?.find((obj) => obj.isoCode === company?.isoCountryCode);
  }, [company?.isoCountryCode]);

  const fieldErrors = useMemo(() => {
    return {
      name: activeValidationErrorCode === InternalErrorCode.ERR_CS_0034,
      zip: activeValidationErrorCode === InternalErrorCode.ERR_CS_0003,
    };
  }, [activeValidationErrorCode]);

  const debouncedValidateLocationResult = useDebounce(validateLocationResult, 200);
  useEffect(() => {
    const { isError, error, isSuccess } = debouncedValidateLocationResult || {};
    let errorCode: string | null = null;

    if (isError && error && 'data' in error) {
      errorCode = (error.data as LocationValidationError).code;
    }

    setActiveValidationErrorCode(errorCode);

    if (isSuccess || isError) {
      setValidationCompleted(true);
    }
  }, [debouncedValidateLocationResult]);

  const onButtonValidation = () => {
    if (
      !location.street ||
      !location.streetNumber ||
      !location.name ||
      !location.city ||
      !location.zipOrPostalCode ||
      !location.latitude ||
      !Number(location.latitude) ||
      Number(location.latitude) < -90 ||
      Number(location.latitude) > 90 ||
      !location.longitude ||
      !Number(location.longitude) ||
      Number(location.longitude) < -180 ||
      Number(location.longitude) > 180 ||
      !location.timeZone ||
      activeValidationErrorCode ||
      debouncedValidateLocationResult?.isLoading ||
      !validationCompleted
    ) {
      return false;
    }

    if (!isSiteTypeValid(location.siteType)) {
      return false;
    }

    const isStateRequired =
      location.isoCountryCode && TAXABLE_COUNTRIES.includes(location.isoCountryCode);
    // if the company is in taxable countries, the stateOrRegion is required and must be valid
    return !(
      isStateRequired &&
      !(location.stateOrRegion && provinceList.find((p) => p.isoCode === location.stateOrRegion))
    );
  };

  const handleSaveClick = () => {
    if (isNew) {
      triggerAddLocation(location);
    } else {
      triggerUpdateLocation(location);
    }
  };

  return (
    <Form
      onSubmit={handleSaveClick}
      queryResult={isNew ? newLocationResult : updateLocationResult}
      onQuerySuccess={() => hideModal()}
      onQueryFailed={() => hideModal()}
    >
      <div className='flex flex-col gap-[16px]'>
        <FormInput
          inputId='location_name'
          label={t('location_name')}
          placeholder={t('location_name')}
          onChange={(event) => handleLocationChange('name', event.target.value)}
          validationType={ValidationType.NOT_EMPTY}
          defaultValue={location.name}
          width='100%'
          dataTestId='locationInput'
          validationQueryResult={fieldErrors.name}
          showErrorIcon={fieldErrors.name}
          errorLabel={fieldErrors.name ? t('invalid_location_name') : ''}
        />
        <div className='flex flex-col gap-2'>
          <AddressAutocomplete
            countryIsoCode={company?.isoCountryCode || 'CA'}
            countryName={country?.name}
            value={location}
            onChange={(next: Partial<AddressValue>) =>
              setLocation((prev) => {
                const updates = {
                  ...next,
                  isoCountryCode: company?.isoCountryCode || '',
                };
                if (updates.latitude !== undefined) {
                  if (typeof updates.latitude === 'string') {
                    const parsed = parseFloat(updates.latitude);
                    updates.latitude = Number.isNaN(parsed) ? updates.latitude : parsed;
                  }
                }
                if (updates.longitude !== undefined) {
                  if (typeof updates.longitude === 'string') {
                    const parsed = parseFloat(updates.longitude);
                    updates.longitude = Number.isNaN(parsed) ? updates.longitude : parsed;
                  }
                }
                return { ...prev, ...updates } as Location;
              })
            }
            defaultMode={defaultLocation ? 'manual' : 'search'}
            include={{
              unit: true,
              state: true,
              country: true,
              timezone: true,
              geo: true,
            }}
            errors={{ zip: fieldErrors.zip }}
            errorLabels={{ zip: t('invalid_zip_postal_code') }}
            stateRequired={Boolean(
              location.isoCountryCode && TAXABLE_COUNTRIES.includes(location.isoCountryCode),
            )}
            onModeChange={() => {}}
          />
        </div>
        <SiteTypeDropdown
          value={location.siteType}
          onChange={(siteType: SiteType | undefined) => handleLocationChange('siteType', siteType)}
          dataTestId='locationSiteType'
        />
        <div className='h-[40px] mt-[8px] mb-[20px]'>
          <Button
            label={t('save')}
            type={ButtonType.PRIMARY}
            disabled={!onButtonValidation()}
            size={ButtonSize.SMALL_FULL}
            isSumbit
            dataTestId='locationSaveButton'
          />
        </div>
      </div>
    </Form>
  );
});

LocationEditor.displayName = 'LocationEditor';
